import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { PackageEditor } from "@/components/admin/PackageEditor";
import { QuoteDetailsFields } from "@/components/admin/QuoteDetailsFields";
import { QuoteFormHeader } from "@/components/admin/QuoteFormHeader";

const EMPTY_QUOTE = {
  id: null,
  slug: null,
  partner1_name: "",
  partner2_name: "",
  client_email: "",
  packages: [],
  video_booth_offer: false,
  second_photographer_offer: false,
  second_photographer_offer_price: 450,
  documentary_edit_offer: false,
  documentary_edit_offer_price: 199,
  prewedding_shoot_offer: false,
  prewedding_shoot_offer_price: 249,
  content_creator_offer: false,
  content_creator_offer_price: 695,
  no_socials: false,
  venue_name: "",
  venue_video_url: "",
  venue_video_urls: [],
  wedding_date: "",
  deposit_type: "",
  deposit_value: "",
  expiry_type: "7_days",
  expiry_date: null,
  status: "draft",
};

function toPayload(quote) {
  return {
    partner1_name: quote.partner1_name,
    partner2_name: quote.partner2_name,
    client_email: quote.client_email,
    packages: quote.packages,
    video_booth_offer: quote.video_booth_offer,
    second_photographer_offer: !!quote.second_photographer_offer,
    second_photographer_offer_price: parseFloat(quote.second_photographer_offer_price) || 450,
    documentary_edit_offer: !!quote.documentary_edit_offer,
    documentary_edit_offer_price: parseFloat(quote.documentary_edit_offer_price) || 199,
    prewedding_shoot_offer: !!quote.prewedding_shoot_offer,
    prewedding_shoot_offer_price: parseFloat(quote.prewedding_shoot_offer_price) || 249,
    content_creator_offer: !!quote.content_creator_offer,
    content_creator_offer_price: parseFloat(quote.content_creator_offer_price) || 695,
    no_socials: !!quote.no_socials,
    venue_name: quote.venue_name || null,
    venue_video_url: quote.venue_video_urls?.[0] || quote.venue_video_url || null,
    venue_video_urls: quote.venue_video_urls?.length ? quote.venue_video_urls : null,
    wedding_date: quote.wedding_date || null,
    deposit_type: quote.deposit_type || null,
    deposit_value:
      quote.deposit_value === "" || quote.deposit_value == null
        ? null
        : parseFloat(quote.deposit_value),
    expiry_type: quote.expiry_type || "7_days",
    expiry_date: quote.expiry_type === "custom" ? quote.expiry_date || null : null,
  };
}

const BREVO_FIELD_LABELS = {
  partner1_name: "Partner 1",
  partner2_name: "Partner 2",
  venue_name: "Venue",
  wedding_date: "Wedding date",
};

export default function QuoteForm({ mode }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [quote, setQuote] = useState(EMPTY_QUOTE);

  useEffect(() => {
    async function init() {
      try {
        if (mode === "edit" && id) {
          const r = await client.get(`/quotes/${id}`);
          const q = r.data;
          if (!q.venue_video_urls?.length && q.venue_video_url) {
            q.venue_video_urls = [q.venue_video_url];
          }
          setQuote(q);
        } else {
          const r = await client.get("/packages/defaults");
          setQuote((q) => ({ ...q, packages: r.data.packages }));
        }
      } catch (err) {
        toast.error("Failed to load");
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [id, mode]);

  function updatePkg(pkgId, next) {
    setQuote((q) => ({
      ...q,
      packages: q.packages.map((p) => (p.id === pkgId ? next : p)),
    }));
  }

  async function brevoLookup() {
    if (!quote.client_email) {
      toast.error("Enter the client email first");
      return;
    }
    setLookingUp(true);
    try {
      const r = await client.get("/brevo/contact-lookup", {
        params: { email: quote.client_email },
      });
      if (!r.data.found) {
        toast.error("No Brevo contact found for that email");
        return;
      }
      const d = r.data;
      const filled = Object.keys(BREVO_FIELD_LABELS).filter((k) => d[k]);
      setQuote((q) => ({
        ...q,
        partner1_name: d.partner1_name || q.partner1_name,
        partner2_name: d.partner2_name || q.partner2_name,
        venue_name: d.venue_name || q.venue_name,
        wedding_date: d.wedding_date || q.wedding_date,
      }));
      if (filled.length) {
        toast.success(
          `Pulled from Brevo: ${filled.map((k) => BREVO_FIELD_LABELS[k]).join(", ")}`
        );
      } else {
        toast.info("Contact found, but no name/venue/date details on it");
      }
    } catch (err) {
      toast.error("Brevo lookup failed");
    } finally {
      setLookingUp(false);
    }
  }

  async function save({ thenSend = false } = {}) {
    if (!quote.partner1_name || !quote.partner2_name || !quote.client_email) {
      toast.error("Names and client email are required");
      return null;
    }
    if (!quote.packages.some((p) => p.selected)) {
      toast.error("Select at least one package");
      return null;
    }
    setSaving(true);
    try {
      let saved;
      if (mode === "create" && !quote.id) {
        const r = await client.post("/quotes", toPayload(quote));
        saved = r.data;
        setQuote(saved);
        toast.success("Quote created");
        if (!thenSend) nav(`/admin/quotes/${saved.id}`, { replace: true });
      } else {
        const r = await client.patch(`/quotes/${quote.id}`, toPayload(quote));
        saved = r.data;
        setQuote(saved);
        toast.success("Quote saved");
      }
      return saved;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function saveAndSend() {
    const saved = await save({ thenSend: true });
    if (!saved) return;
    setSending(true);
    try {
      await client.post(`/quotes/${saved.id}/send`);
      toast.success(`Quote sent to ${saved.client_email}`);
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  }

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C5A56]">
        <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="quote-form-page">
      <QuoteFormHeader
        slug={quote.slug}
        saving={saving}
        sending={sending}
        onSave={() => save()}
        onSaveAndSend={saveAndSend}
      />

      <main className="px-6 md:px-12 lg:px-16 py-12 max-w-5xl mx-auto">
        <div className="mb-12 fade-up">
          <div className="text-xs tracking-overline uppercase text-[#A9927D] mb-3 font-medium">
            {mode === "edit" ? "Edit quote" : "New quote"}
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-none text-[#1F1E1D]">
            Build the quote.
          </h1>
        </div>

        <section className="bg-white border border-[#E5E3DB] p-8 md:p-10 mb-12 fade-up delay-1">
          <QuoteDetailsFields
            quote={quote}
            setQuote={setQuote}
            onBrevoLookup={brevoLookup}
            lookingUp={lookingUp}
          />
        </section>

        <section className="fade-up delay-2">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-display text-3xl text-[#1F1E1D]">Packages</h2>
            <p className="text-xs tracking-overline uppercase text-[#8A8782] font-medium">
              Tick the packages to include. Prices are editable.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5">
            {quote.packages.map((p) => (
              <PackageEditor
                key={p.id}
                pkg={p}
                onChange={(next) => updatePkg(p.id, next)}
              />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
